import * as i0 from '@angular/core';
import { InjectionToken, Injectable, Inject, Directive, Input, NgModule } from '@angular/core';
import { BehaviorSubject, from, interval, of, Subject, fromEvent } from 'rxjs';
import { skip, filter, switchMap, take, takeWhile, takeUntil } from 'rxjs/operators';
import * as i1 from '@angular/router';
import { NavigationEnd } from '@angular/router';

var EventType;
(function (EventType) {
    EventType["BeginCheckout"] = "beginCheckout";
    EventType["AddToCart"] = "addToCart";
    EventType["RemoveFromCart"] = "removeFromCart";
    EventType["AddPayment"] = "addPayment";
    EventType["Purcahse"] = "purchase";
})(EventType || (EventType = {}));

const FS_ANALYTICS_CONFIG = new InjectionToken('fs-analytics-config');

class Provider {
    constructor(_injector, _config, _router) {
        this._injector = _injector;
        this._config = _config;
        this._router = _router;
        this._router.events.pipe(skip(1), filter(event => event instanceof NavigationEnd))
            .subscribe((event) => {
            this.trackPage(event.urlAfterRedirects);
        });
    }
    get window() {
        return window;
    }
    addScript(src) {
        return new Promise((resolve, error) => {
            var script = document.createElement('script');
            script.src = src;
            script.async = true;
            script.setAttribute('async', 'true');
            script.onload = () => {
                resolve();
            };
            script.onerror = () => {
                error();
            };
            this.appendHead(script);
        });
    }
    appendHead(el) {
        document.getElementsByTagName('head')[0].appendChild(el);
    }
}

class FacebookPixelProvider extends Provider {
    init() {
        this.addScript();
        this.addImg();
    }
    trackEvent(type, value, options) {
        let data = Object.assign(Object.assign({}, options), { value });
        if (type === EventType.Purcahse) {
            const prucahseEvent = value;
            type = 'Purchase';
            data = {
                currency: prucahseEvent.currency,
                value: prucahseEvent.total,
                contents: prucahseEvent.items
                    .map((product) => ({
                    id: product.id,
                    quantity: product.quantity || 1,
                }))
            };
        }
        fbq('track', type, data);
    }
    addScript() {
        const f = window;
        const n = f.fbq = function () {
            n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
        };
        if (!f._fbq) {
            f._fbq = n;
        }
        n.push = n;
        n.loaded = !0;
        n.version = '2.0';
        n.queue = [];
        return new Promise((resolve, error) => {
            super.addScript(`https://connect.facebook.net/en_US/fbevents.js`)
                .then(() => {
                fbq('init', this.pixelId);
                this.trackEvent('PageView');
                resolve();
            })
                .catch(error);
        });
    }
    trackPage(path) {
        this.trackEvent('PageView', { path });
    }
    setUser(data) {
    }
    addImg() {
        var img = document.createElement('img');
        img.setAttribute('height', '1');
        img.setAttribute('width', '1');
        img.setAttribute('style', 'display:none');
        img.setAttribute('src', `https://www.facebook.com/tr?id=${this.pixelId}&ev=PageView&noscript=1`);
        var noscript = document.createElement('noscript');
        noscript.append(img);
        this.appendHead(noscript);
    }
    get pixelId() {
        var _a;
        return (_a = this._config.providers.facebookPixel) === null || _a === void 0 ? void 0 : _a.pixelId;
    }
}

class GoogleAnalyticsProvider extends Provider {
    init() {
        if (this.measurementId) {
            this.addScript(`https://www.googletagmanager.com/gtag/js?id=${this.measurementId}`);
            this.window.dataLayer = this.window.dataLayer || [];
            this.window.gtag = function () {
                window.dataLayer.push(arguments);
            };
            gtag('js', new Date());
            gtag('config', this.measurementId, { path_path: this._router.url });
        }
    }
    trackEvent(type, value, options) {
        let data = {
            event_category: options === null || options === void 0 ? void 0 : options.category,
            event_label: options === null || options === void 0 ? void 0 : options.label,
            value: value
        };
        if (type === EventType.Purcahse) {
            const prucahseEvent = value;
            data = {
                transaction_id: prucahseEvent.transactionId,
                value: prucahseEvent.total,
                tax: prucahseEvent.tax,
                shipping: prucahseEvent.shipping,
                currency: prucahseEvent.currency,
                items: prucahseEvent.items
                    .map((product) => ({
                    item_id: product.id,
                    item_name: product.name,
                    price: product.price,
                    quantity: product.quantity || 1,
                }))
            };
        }
        this.gtag('event', type, data);
    }
    trackPage(path) {
        gtag('event', 'page_view', {
            page_path: path,
            send_to: this.measurementId
        });
    }
    gtag(name, value, options = {}) {
        this.window.gtag(name, value, options);
    }
    setUser(data) {
    }
    get measurementId() {
        var _a;
        return (_a = this._config.providers.googleAnalytics) === null || _a === void 0 ? void 0 : _a.measurementId;
    }
}

class GoogleTagManagerProvider extends Provider {
    constructor() {
        super(...arguments);
        this._initData = [];
        this._init$ = new BehaviorSubject(false);
    }
    init() {
        if (this.containerId) {
            const scriptDomain = this.scriptDomain || 'www.googletagmanager.com';
            from(this.addScript(`https://${scriptDomain}/gtm.js?id=${this.containerId}`))
                .pipe(switchMap(() => interval(100)), take(1000), takeWhile(() => !this._init$.getValue()), filter(() => {
                return this.window.dataLayer.some((item) => item.event === 'gtm.load');
            }))
                .subscribe(() => {
                this._init$.next(true);
                this._init$.complete();
                this._initData.forEach((data) => {
                    this.window.dataLayer.push(data);
                });
            });
            this.window.dataLayer = this.window.dataLayer || [];
            this.window.dataLayer.push({
                event: 'gtm.js',
                'gtm.start': new Date().getTime(),
            });
            this.pushData('js', new Date());
            this.pushData('config', this.containerId, { path_path: this._router.url });
        }
    }
    pushData(...data) {
        this.window.dataLayer.push(data);
    }
    trackPage(path) {
        this.trackEvent('pageview', {
            page: {
                path,
            }
        });
    }
    trackEvent(type, value, options) {
        const mapping = this._getMapping(type);
        const data = mapping.transform ?
            mapping.transform(mapping.type, value) : {
            event: type,
            value,
            category: options === null || options === void 0 ? void 0 : options.category,
            label: options === null || options === void 0 ? void 0 : options.label,
        };
        const dataLayer = this._init$.getValue() ?
            this.window.dataLayer :
            this._initData;
        if (mapping.ecommerce) {
            dataLayer.push({ ecommerce: null });
        }
        dataLayer.push(data);
    }
    setUser(data) { }
    get containerId() {
        var _a;
        return (_a = this._config.providers.googleTagManager) === null || _a === void 0 ? void 0 : _a.containerId;
    }
    get scriptDomain() {
        var _a;
        return (_a = this._config.providers.googleTagManager) === null || _a === void 0 ? void 0 : _a.scriptDomain;
    }
    _mapPurchaseEventData(event, value) {
        return {
            event,
            ecommerce: {
                transaction_id: value.transactionId,
                value: value.total,
                tax: value.tax,
                shipping: value.shipping,
                currency: value.currency,
                items: this._mapItems(value.items),
            }
        };
    }
    _mapBeginCheckoutEventData(event, value) {
        return {
            event,
            ecommerce: {
                value: value.total,
                currency: value.currency,
                items: this._mapItems(value.items),
            }
        };
    }
    _mapAddToCartEventData(event, value) {
        return {
            event,
            ecommerce: {
                value: value.total,
                currency: value.currency,
                items: this._mapItems(value.items),
            }
        };
    }
    _mapRemoveFromCartEventData(event, value) {
        return {
            event,
            ecommerce: {
                value: value.total,
                currency: value.currency,
                items: this._mapItems(value.items),
            }
        };
    }
    _mapAddPaymentEventData(event, value) {
        return {
            event,
            ecommerce: {
                value: value.total,
                currency: value.currency,
                payment_type: value.paymentType,
                items: this._mapItems(value.items),
            }
        };
    }
    _mapItems(items) {
        return items
            .map((item) => ({
            item_id: item.id,
            item_name: item.name,
            price: item.price || 0,
            quantity: item.quantity || 1,
            item_category: item.category,
            item_category2: item.category2,
        }));
    }
    _getMapping(type) {
        let transform;
        if (type === EventType.Purcahse) {
            type = 'purchase';
            transform = (event, value) => this._mapPurchaseEventData(event, value);
        }
        else if (type === EventType.BeginCheckout) {
            type = 'begin_checkout';
            transform = (event, value) => this._mapBeginCheckoutEventData(event, value);
        }
        else if (type === EventType.AddPayment) {
            type = 'add_payment_info';
            transform = (event, value) => this._mapAddPaymentEventData(event, value);
        }
        else if (type === EventType.AddToCart) {
            type = 'add_to_cart';
            transform = (event, value) => this._mapAddToCartEventData(event, value);
        }
        else if (type === EventType.RemoveFromCart) {
            type = 'remove_from_cart';
            transform = (event, value) => this._mapRemoveFromCartEventData(event, value);
        }
        return { type, transform, ecommerce: !!transform };
    }
}

class KlaviyoProvider extends Provider {
    init() {
        window._learnq = window._learnq || [];
        if (this.publicApiKey) {
            this.addScript(`https://static.klaviyo.com/onsite/js/klaviyo.js?company_id=${this.publicApiKey}`);
        }
    }
    trackPage(path) {
        this.trackEvent('Page View', { path });
    }
    purchase(data) {
        this.trackEvent('Purchase', data);
    }
    trackEvent(action, value) {
        window._learnq.push(['track', action, value]);
    }
    setUser(data) {
        data = Object.keys(data)
            .reduce((accum, name) => {
            switch (name) {
                case 'email':
                    accum['$email'] = data[name];
                    break;
                case 'firstName':
                    accum['$first_name'] = data[name];
                    break;
                case 'lastName':
                    accum['$last_name'] = data[name];
                    break;
                default:
                    accum[name] = data[name];
            }
            return accum;
        }, {});
        window._learnq.push(['identify', data]);
    }
    get publicApiKey() {
        var _a;
        return (_a = this._config.providers.klaviyo) === null || _a === void 0 ? void 0 : _a.publicApiKey;
    }
}

class AnalyticsProcessorService {
    process(event) {
        return of(event);
    }
}
AnalyticsProcessorService.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: AnalyticsProcessorService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
AnalyticsProcessorService.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: AnalyticsProcessorService, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: AnalyticsProcessorService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }] });

class FsAnalytics {
    constructor(_config, _injector, _router) {
        this._config = _config;
        this._injector = _injector;
        this._router = _router;
        this._providers = [];
    }
    init() {
        this._processorService = this._config.processor || new AnalyticsProcessorService();
        if (this._config.providers.googleAnalytics) {
            this._providers.push(new GoogleAnalyticsProvider(this._injector, this._config, this._router));
        }
        if (this._config.providers.googleTagManager) {
            this._providers.push(new GoogleTagManagerProvider(this._injector, this._config, this._router));
        }
        if (this._config.providers.facebookPixel) {
            this._providers.push(new FacebookPixelProvider(this._injector, this._config, this._router));
        }
        if (this._config.providers.klaviyo) {
            this._providers.push(new KlaviyoProvider(this._injector, this._config, this._router));
        }
        this._providers.forEach((provider) => {
            provider.init();
        });
    }
    addToCart(data) {
        this.trackEvent(EventType.AddToCart, data);
    }
    addPayment(data) {
        this.trackEvent(EventType.AddPayment, data);
    }
    removeFromCart(data) {
        this.trackEvent(EventType.RemoveFromCart, data);
    }
    beginCheckout(event) {
        event = Object.assign(Object.assign({}, event), { total: event.total || 0, items: (event.items || [])
                .map((product) => (Object.assign(Object.assign({}, product), { price: product.price || 0, quantity: product.quantity || 1 }))) });
        this.trackEvent(EventType.BeginCheckout, event);
    }
    purchase(event) {
        event = Object.assign(Object.assign({}, event), { total: event.total || 0, shipping: event.shipping || 0, tax: event.tax || 0, items: (event.items || [])
                .map((product) => (Object.assign(Object.assign({}, product), { price: product.price || 0, quantity: product.quantity || 1 }))) });
        this.trackEvent(EventType.Purcahse, event);
    }
    trackEvent(type, data) {
        this._processorService.process({ type, data })
            .pipe(take(1))
            .subscribe(() => {
            this._providers
                .forEach((provider) => {
                provider.trackEvent(type, data);
            });
        });
    }
    setUser(data) {
        this._providers.forEach((provider) => {
            provider.setUser(data);
        });
    }
}
FsAnalytics.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsAnalytics, deps: [{ token: FS_ANALYTICS_CONFIG }, { token: i0.Injector }, { token: i1.Router }], target: i0.ɵɵFactoryTarget.Injectable });
FsAnalytics.ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsAnalytics, providedIn: 'root' });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsAnalytics, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root',
                }]
        }], ctorParameters: function () { return [{ type: undefined, decorators: [{
                    type: Inject,
                    args: [FS_ANALYTICS_CONFIG]
                }] }, { type: i0.Injector }, { type: i1.Router }]; } });

class FsAnalyticsDirective {
    constructor(_analytics, _el) {
        this._analytics = _analytics;
        this._el = _el;
        this.action = '';
        this.event = 'click';
        this._destroy$ = new Subject();
    }
    ngOnInit() {
        fromEvent(this._el.nativeElement, this.event)
            .pipe(takeUntil(this._destroy$))
            .subscribe(() => {
            this._analytics.trackEvent(this.action);
        });
    }
    ngOnDestroy() {
        this._destroy$.next(null);
        this._destroy$.complete();
    }
}
FsAnalyticsDirective.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsAnalyticsDirective, deps: [{ token: FsAnalytics }, { token: i0.ElementRef }], target: i0.ɵɵFactoryTarget.Directive });
FsAnalyticsDirective.ɵdir = i0.ɵɵngDeclareDirective({ minVersion: "12.0.0", version: "12.2.16", type: FsAnalyticsDirective, selector: "[fsAnalytics]", inputs: { action: ["fsAnalytics", "action"], event: "event" }, ngImport: i0 });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsAnalyticsDirective, decorators: [{
            type: Directive,
            args: [{
                    selector: '[fsAnalytics]'
                }]
        }], ctorParameters: function () { return [{ type: FsAnalytics }, { type: i0.ElementRef }]; }, propDecorators: { action: [{
                type: Input,
                args: ['fsAnalytics']
            }], event: [{
                type: Input
            }] } });

class FsAnalyticsModule {
    static forRoot(config = {}) {
        return {
            ngModule: FsAnalyticsModule,
            // providers: [
            //   {
            //     useClass:
            //   }
            // ]
        };
    }
}
FsAnalyticsModule.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsAnalyticsModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
FsAnalyticsModule.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsAnalyticsModule, declarations: [FsAnalyticsDirective], exports: [FsAnalyticsDirective] });
FsAnalyticsModule.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsAnalyticsModule });
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "12.2.16", ngImport: i0, type: FsAnalyticsModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [
                        FsAnalyticsDirective,
                    ],
                    exports: [
                        FsAnalyticsDirective,
                    ]
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { EventType, FS_ANALYTICS_CONFIG, FacebookPixelProvider, FsAnalytics, FsAnalyticsDirective, FsAnalyticsModule, GoogleAnalyticsProvider, GoogleTagManagerProvider, KlaviyoProvider, Provider };
